// src/pages/VehicleV2/VehicleTransferApproval.jsx
// Duplicate "Select" options removed from all dropdowns.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaSyncAlt,
  FaArrowLeft,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/vehicleTransferApproval`;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const normalizeSelect = (v, fallback = "Select") => {
  const s = clean(v);
  return s || fallback;
};

const defaultForm = {
  vehicleMainType: "Select",
  store: "Select",
  vehicleCode: "Select",
  vehicleRegNo: "Select",
  status: "Select",
  month: "Select",
  year: "Select",
  transferTo: "Select",
  transferFromLoc: "Select",
  transferToLoc: "Select",
};

const optVal = (x, ...keys) => {
  for (const k of keys) {
    if (x?.[k] !== undefined && x?.[k] !== null && String(x[k]).trim() !== "") {
      return String(x[k]);
    }
  }
  return "";
};

const isPlaceholderOption = (x) => {
  if (x === null || x === undefined) return true;
  if (typeof x === "string" || typeof x === "number") {
    const s = String(x).trim().toLowerCase();
    return !s || s === "select" || s === "--select--" || s === "all";
  }
  const value = clean(
    x.value ??
      x.id ??
      x.fld_vehicletypeindex ??
      x.fld_Branchid ??
      x.fld_storeid ??
      x.locationId ??
      x.label ??
      x.name ??
      ""
  ).toLowerCase();
  const label = clean(x.label ?? x.name ?? x.fld_vehicletypename ?? x.fld_BranchName ?? x.fld_storename ?? "").toLowerCase();
  return !value || value === "select" || value === "all" || label === "select" || label === "all";
};

const uniqueOptions = (arr) => {
  const list = Array.isArray(arr) ? arr : [];
  const seen = new Set();
  const out = [];
  for (const x of list) {
    if (isPlaceholderOption(x)) continue;
    const key =
      typeof x === "object"
        ? `${optVal(x, "value", "id", "fld_vehicletypeindex", "fld_Branchid", "fld_storeid", "locationId")}|${optVal(x, "label", "name", "fld_vehicletypename", "fld_BranchName", "fld_storename")}`
        : String(x).trim();
    if (!key || seen.has(key.toLowerCase())) continue;
    seen.add(key.toLowerCase());
    out.push(x);
  }
  return out;
};

const VehicleTransferApproval = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS =
    getLS("branchId") || getLS("Branchid") || getLS("branchid") || "";
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId") || "0";

  const branchId = useMemo(() => String(toInt(branchIdLS, 0)), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim() || "HO",
    [branchTypeLS]
  );
  const userId = useMemo(() => String(userIdLS || "").trim() || "0", [userIdLS]);
  const loginUserId =
    (typeof window !== "undefined"
      ? sessionStorage.getItem("loginUserId")
      : "") || userId;

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const [filterType, setFilterType] = useState("own");
  const [transferType, setTransferType] = useState("branch");
  const [form, setForm] = useState(defaultForm);

  const [init, setInit] = useState({
    years: [],
    months: [],
    statusOptions: [
      { label: "Approved", value: "1" },
      { label: "Waiting", value: "0" },
    ],
    stores: [],
    vehicleTypes: [],
    branches: [],
    fromLocations: [],
    toLocations: [],
    fromBranchName: "",
  });

  const [vehicleCodeDD, setVehicleCodeDD] = useState([]);
  const [vehicleRegNoDD, setVehicleRegNoDD] = useState([]);
  const [rows, setRows] = useState([]);
  const [checked, setChecked] = useState({});
  const [approvedBy, setApprovedBy] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const isBusy =
    pageLoading || dropdownLoading || gridLoading || approveLoading;
  const isSpares = filterType === "spares";

  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const getTransferApiType = useCallback(
    () => (transferType === "branch" ? "B" : "L"),
    [transferType]
  );

  const getRowKey = useCallback((r, idx) => {
    return String(
      r?.fld_vehicle_trandferid ??
        r?.fld_spare_trandferid ??
        r?.fld_vehicle_transferid ??
        r?.vehicleTransferId ??
        r?.spareTransferId ??
        r?.id ??
        idx
    );
  }, []);

  const isApprovedRow = useCallback((r) => {
    const v =
      r?.fld_vehicle_isapproved ??
      r?.vehicle_isapproved ??
      r?.isapproved ??
      r?.isApproved;
    return String(v).toLowerCase() === "true" || String(v) === "1";
  }, []);

  const col = useCallback((r, ...keys) => {
    for (const k of keys) {
      if (r?.[k] !== undefined && r?.[k] !== null && String(r[k]).trim() !== "") {
        return r[k];
      }
    }
    return "";
  }, []);

  const vehicleTypes = useMemo(() => uniqueOptions(init.vehicleTypes), [init.vehicleTypes]);
  const branches = useMemo(() => uniqueOptions(init.branches), [init.branches]);
  const stores = useMemo(() => uniqueOptions(init.stores), [init.stores]);
  const fromLocations = useMemo(() => uniqueOptions(init.fromLocations), [init.fromLocations]);
  const toLocations = useMemo(() => uniqueOptions(init.toLocations), [init.toLocations]);
  const months = useMemo(() => uniqueOptions(init.months), [init.months]);
  const years = useMemo(() => uniqueOptions(init.years), [init.years]);
  const vehicleCodes = useMemo(() => uniqueOptions(vehicleCodeDD), [vehicleCodeDD]);
  const vehicleRegNos = useMemo(() => uniqueOptions(vehicleRegNoDD), [vehicleRegNoDD]);
  const statusOptions = useMemo(() => {
    const raw = Array.isArray(init.statusOptions) ? init.statusOptions : [];
    const mapped = raw.map((s) =>
      typeof s === "object"
        ? { label: String(s.label ?? s.value ?? ""), value: String(s.value ?? "") }
        : { label: String(s), value: String(s) }
    );
    return uniqueOptions(mapped);
  }, [init.statusOptions]);

  const buildVehicleDropdownParams = useCallback(
    () => ({
      type: getTransferApiType(),
      branchId: branchType === "HO" ? "%" : branchId,
      branchType,
      status: normalizeSelect(form.status),
      store: normalizeSelect(form.store),
      loginUserId,
    }),
    [getTransferApiType, branchId, branchType, form.status, form.store, loginUserId]
  );

  const buildListParams = useCallback(
    (activeForm, activeFilterType = filterType, activeTransferType = transferType) => {
      const type = activeTransferType === "branch" ? "B" : "L";
      const params = {
        branch: branchType,
        type,
        branchId,
        branchType,
        status: normalizeSelect(activeForm.status),
        transferTo: normalizeSelect(activeForm.transferTo),
        fromLoc:
          activeTransferType === "location"
            ? normalizeSelect(activeForm.transferFromLoc)
            : "Select",
        toLoc:
          activeTransferType === "location"
            ? normalizeSelect(activeForm.transferToLoc)
            : "Select",
        vehicleCode: normalizeSelect(activeForm.vehicleCode),
        vehicleRegNo: normalizeSelect(activeForm.vehicleRegNo),
        store: normalizeSelect(activeForm.store),
        loginUserId,
        month: normalizeSelect(activeForm.month),
        year: normalizeSelect(activeForm.year),
        vehicletype: normalizeSelect(activeForm.vehicleMainType),
      };
      if (activeFilterType === "spares") {
        params.vehicleCode = "Select";
        params.vehicleRegNo = "Select";
      }
      return params;
    },
    [filterType, transferType, branchType, branchId, loginUserId]
  );

  const loadInit = useCallback(async () => {
    if (!toInt(loginUserId) || !toInt(branchId)) {
      toast.error("branchId / loginUserId missing");
      return;
    }
    try {
      setPageLoading(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: { branchId, branchType, loginUserId },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Init failed");
      setInit({
        years: Array.isArray(data?.years) ? data.years : [],
        months: Array.isArray(data?.months) ? data.months : [],
        statusOptions: Array.isArray(data?.statusOptions)
          ? data.statusOptions
          : [
              { label: "Approved", value: "1" },
              { label: "Waiting", value: "0" },
            ],
        stores: Array.isArray(data?.stores) ? data.stores : [],
        vehicleTypes: Array.isArray(data?.vehicleTypes) ? data.vehicleTypes : [],
        branches: Array.isArray(data?.branches) ? data.branches : [],
        fromLocations: Array.isArray(data?.fromLocations) ? data.fromLocations : [],
        toLocations: Array.isArray(data?.toLocations) ? data.toLocations : [],
        fromBranchName: data?.fromBranchName || "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Init failed");
    } finally {
      setPageLoading(false);
    }
  }, [branchId, branchType, loginUserId, commonHeaders]);

  const loadVehicleDropdowns = useCallback(async () => {
    if (!toInt(loginUserId)) return;
    try {
      setDropdownLoading(true);
      const params = buildVehicleDropdownParams();
      const [codeRs, regRs] = await Promise.all([
        axios.get(`${API_BASE}/dropdowns/vehicleCode`, { params, headers: commonHeaders }),
        axios.get(`${API_BASE}/dropdowns/vehicleRegNo`, { params, headers: commonHeaders }),
      ]);
      setVehicleCodeDD(
        codeRs?.data?.success && Array.isArray(codeRs?.data?.list) ? codeRs.data.list : []
      );
      setVehicleRegNoDD(
        regRs?.data?.success && Array.isArray(regRs?.data?.list) ? regRs.data.list : []
      );
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Dropdown load failed");
    } finally {
      setDropdownLoading(false);
    }
  }, [buildVehicleDropdownParams, commonHeaders, loginUserId]);

  const loadList = useCallback(
    async ({
      silent = false,
      activeForm = form,
      activeFilterType = filterType,
      activeTransferType = transferType,
    } = {}) => {
      if (!toInt(loginUserId) || !toInt(branchId)) {
        if (!silent) toast.error("branchId / loginUserId missing");
        return;
      }
      try {
        setGridLoading(true);
        setChecked({});
        setApprovedBy({});
        setPage(1);
        const url =
          activeFilterType === "own" ? `${API_BASE}/list` : `${API_BASE}/list/spares`;
        const { data } = await axios.get(url, {
          params: buildListParams(activeForm, activeFilterType, activeTransferType),
          headers: commonHeaders,
        });
        if (!data?.success) {
          setRows([]);
          if (!silent) toast.warn(data?.message || "No data found");
          return;
        }
        const list = Array.isArray(data?.list) ? data.list : [];
        setRows(list);
        if (!silent) toast.success(`Found ${data?.count ?? list.length} record(s)`);
      } catch (err) {
        setRows([]);
        toast.error(err?.response?.data?.message || err.message || "Search failed");
      } finally {
        setGridLoading(false);
      }
    },
    [form, filterType, transferType, loginUserId, branchId, buildListParams, commonHeaders]
  );

  useEffect(() => {
    const boot = async () => {
      await loadInit();
      await loadVehicleDropdowns();
      await loadList({ silent: true, activeForm: defaultForm });
      setHasSearched(true);
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadVehicleDropdowns();
  }, [form.store, form.status, transferType, loadVehicleDropdowns]);

  const onFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleFilterTypeChange = useCallback((value) => {
    setFilterType(value);
    if (value === "spares") {
      setForm((prev) => ({ ...prev, vehicleCode: "Select", vehicleRegNo: "Select" }));
    }
  }, []);

  const handleTransferTypeChange = useCallback((value) => {
    setTransferType(value);
    if (value === "branch") {
      setForm((prev) => ({
        ...prev,
        transferFromLoc: "Select",
        transferToLoc: "Select",
      }));
    } else {
      setForm((prev) => ({ ...prev, transferTo: "Select" }));
    }
  }, []);

  const handleSearch = useCallback(async () => {
    setHasSearched(true);
    await loadList({
      silent: false,
      activeForm: form,
      activeFilterType: filterType,
      activeTransferType: transferType,
    });
  }, [loadList, form, filterType, transferType]);

  const handleViewAll = useCallback(async () => {
    const reset = { ...defaultForm };
    setForm(reset);
    setChecked({});
    setApprovedBy({});
    setHasSearched(true);
    await loadVehicleDropdowns();
    await loadList({
      silent: false,
      activeForm: reset,
      activeFilterType: filterType,
      activeTransferType: transferType,
    });
  }, [loadVehicleDropdowns, loadList, filterType, transferType]);

  const handleRefresh = useCallback(async () => {
    await loadInit();
    await loadVehicleDropdowns();
    await loadList({ silent: true, activeForm: form });
  }, [loadInit, loadVehicleDropdowns, loadList, form]);

  const toggleRow = useCallback((rowKey) => {
    setChecked((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  }, []);

  const approveSelected = useCallback(async () => {
    const selectedKeys = Object.keys(checked).filter((k) => checked[k]);
    if (!selectedKeys.length) {
      toast.info("Select at least one row");
      return;
    }
    try {
      setApproveLoading(true);
      const approvals = [];
      rows.forEach((r, idx) => {
        const key = String(getRowKey(r, idx));
        if (!checked[key]) return;
        const by = clean(approvedBy[key]);
        if (!by) return;
        if (filterType === "own") {
          const vehicleTransferId = toInt(
            r?.fld_vehicle_trandferid ?? r?.vehicleTransferId ?? r?.id
          );
          if (vehicleTransferId) approvals.push({ vehicleTransferId, approvedBy: by });
        } else {
          const spareTransferId = toInt(
            r?.fld_spare_trandferid ?? r?.spareTransferId ?? r?.id
          );
          if (spareTransferId) approvals.push({ spareTransferId, approvedBy: by });
        }
      });
      if (!approvals.length) {
        toast.info("Enter Approved By for selected rows");
        return;
      }
      const url =
        filterType === "own" ? `${API_BASE}/approve` : `${API_BASE}/approve/spares`;
      const payload =
        filterType === "own" ? { ipAddress: "", approvals } : { approvals };
      const { data } = await axios.post(url, payload, { headers: commonHeaders });
      if (!data?.success) throw new Error(data?.message || "Approval failed");
      toast.success(data?.message || "Approved successfully");
      await loadList({
        silent: true,
        activeForm: form,
        activeFilterType: filterType,
        activeTransferType: transferType,
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Approval failed");
    } finally {
      setApproveLoading(false);
    }
  }, [
    checked,
    approvedBy,
    rows,
    getRowKey,
    filterType,
    commonHeaders,
    loadList,
    form,
    transferType,
  ]);

  const totalRecords = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize) || 1);
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const pageStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageRows = useMemo(
    () => rows.slice(pageStart, pageStart + pageSize),
    [rows, pageStart, pageSize]
  );
  const pageEnd = pageStart + pageRows.length;

  const pageNumbers = useMemo(() => {
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    const list = [];
    for (let i = start; i <= end; i += 1) list.push(i);
    return list;
  }, [currentPage, totalPages]);

  const pagerBtn = (active) =>
    `min-w-[36px] h-9 px-3 rounded-lg text-sm font-semibold border ${
      active
        ? `text-white ${buttonClass} border-transparent`
        : isDark
        ? "bg-gray-700 text-white border-gray-600 hover:bg-gray-600"
        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
    }`;

  const renderSelect = (name, value, options, disabled, getValue, getLabel) => (
    <select
      name={name}
      value={value}
      onChange={onFormChange}
      className={inputClass}
      disabled={disabled}
    >
      <option value="Select">Select</option>
      {options.map((x, i) => (
        <option key={`${name}-${getValue(x)}-${i}`} value={getValue(x)}>
          {getLabel(x)}
        </option>
      ))}
    </select>
  );

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TRANSFER APPROVAL</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Transfer Approval
              </h2>
              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                INFRA SCREEN : B-24 • Branch {branchId || "-"} • {branchType} •{" "}
                {isSpares ? "Spares & Catalogue" : "Own Vehicle"} •{" "}
                {transferType === "branch" ? "Branch" : "Location"} • Records {totalRecords}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                  isDark
                    ? "bg-gray-700 text-white border-gray-600"
                    : "bg-gray-100 text-gray-700 border-gray-300"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className={isBusy ? "animate-spin" : ""} />
                Refresh
              </button>
              <button
                type="button"
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                  isDark
                    ? "bg-gray-700 text-white border-gray-600"
                    : "bg-gray-100 text-gray-700 border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={handleViewAll}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200"
                        : "border-gray-300 text-gray-700"
                    }`}
                  >
                    <FaEye /> View All
                  </button>
                  <button
                    type="button"
                    onClick={handleSearch}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold text-white ${buttonClass} ${hoverClass} ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaSearch /> {gridLoading ? "Searching..." : "Search"}
                  </button>
                  <button
                    type="button"
                    onClick={approveSelected}
                    disabled={isBusy}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold text-white bg-green-600 hover:bg-green-700 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaCheck /> {approveLoading ? "Approving..." : "Approve"}
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label className={labelClass}>Vehicle Type</label>
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="filterType"
                      value="own"
                      checked={!isSpares}
                      onChange={() => handleFilterTypeChange("own")}
                      disabled={isBusy}
                    />
                    <span>Own Vehicle</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="filterType"
                      value="spares"
                      checked={isSpares}
                      onChange={() => handleFilterTypeChange("spares")}
                      disabled={isBusy}
                    />
                    <span>Spares And Catelogue</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Vehicle Main Type</label>
                  {renderSelect(
                    "vehicleMainType",
                    form.vehicleMainType,
                    vehicleTypes,
                    isBusy,
                    (x) => optVal(x, "value", "id", "fld_vehicletypeindex") || "Select",
                    (x) => optVal(x, "label", "name", "fld_vehicletypename", "value") || "Select"
                  )}
                </div>
                <div>
                  <label className={labelClass}>Transfer To</label>
                  {renderSelect(
                    "transferTo",
                    form.transferTo,
                    branches,
                    transferType !== "branch" || isBusy,
                    (x) => optVal(x, "value", "id", "fld_Branchid", "branchId") || "Select",
                    (x) => optVal(x, "label", "name", "fld_BranchName", "branchName") || "Select"
                  )}
                </div>
                <div>
                  <label className={labelClass}>Store</label>
                  {renderSelect(
                    "store",
                    form.store,
                    stores,
                    isBusy,
                    (x) => optVal(x, "value", "id", "fld_storeid") || "Select",
                    (x) => optVal(x, "label", "name", "fld_storename") || "Select"
                  )}
                </div>
                <div>
                  <label className={labelClass}>Transfer Type</label>
                  <div className="flex flex-wrap gap-6 mt-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="transferType"
                        checked={transferType === "branch"}
                        onChange={() => handleTransferTypeChange("branch")}
                        disabled={isBusy}
                      />
                      <span>Branch</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="transferType"
                        checked={transferType === "location"}
                        onChange={() => handleTransferTypeChange("location")}
                        disabled={isBusy}
                      />
                      <span>Location</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  {renderSelect(
                    "vehicleCode",
                    form.vehicleCode,
                    vehicleCodes,
                    isSpares || isBusy,
                    (x) => optVal(x, "value", "id") || "Select",
                    (x) => optVal(x, "label", "name") || "Select"
                  )}
                </div>
                <div>
                  <label className={labelClass}>Transfer From</label>
                  <div className={`${inputClass} flex items-center min-h-[42px]`}>
                    {init.fromBranchName || "-"}
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Vehicle Number</label>
                  {renderSelect(
                    "vehicleRegNo",
                    form.vehicleRegNo,
                    vehicleRegNos,
                    isSpares || isBusy,
                    (x) => optVal(x, "value", "id") || "Select",
                    (x) => optVal(x, "label", "name") || "Select"
                  )}
                </div>
                <div>
                  <label className={labelClass}>From Location</label>
                  {renderSelect(
                    "transferFromLoc",
                    form.transferFromLoc,
                    fromLocations,
                    transferType !== "location" || isBusy,
                    (x) => optVal(x, "value", "id", "locationId") || "Select",
                    (x) => optVal(x, "label", "name", "locationName") || "Select"
                  )}
                </div>
                <div>
                  <label className={labelClass}>Status</label>
                  {renderSelect(
                    "status",
                    form.status,
                    statusOptions,
                    isBusy,
                    (x) => x.value,
                    (x) => x.label
                  )}
                </div>
                <div>
                  <label className={labelClass}>To Location</label>
                  {renderSelect(
                    "transferToLoc",
                    form.transferToLoc,
                    toLocations,
                    transferType !== "location" || isBusy,
                    (x) => optVal(x, "value", "id", "locationId") || "Select",
                    (x) => optVal(x, "label", "name", "locationName") || "Select"
                  )}
                </div>
                <div>
                  <label className={labelClass}>Month</label>
                  {renderSelect(
                    "month",
                    form.month,
                    months,
                    isBusy,
                    (x) => (typeof x === "object" ? String(x.value ?? x.label ?? "") : String(x)),
                    (x) => (typeof x === "object" ? String(x.label ?? x.value ?? "") : String(x))
                  )}
                </div>
                <div>
                  <label className={labelClass}>Year</label>
                  {renderSelect(
                    "year",
                    form.year,
                    years,
                    isBusy,
                    (x) => (typeof x === "object" ? String(x.value ?? x.label ?? "") : String(x)),
                    (x) => (typeof x === "object" ? String(x.label ?? x.value ?? "") : String(x))
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="overflow-hidden rounded-lg border shadow-lg">
            {pageLoading || gridLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                Loading...
              </div>
            ) : pageRows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                {hasSearched ? "No Records Found!" : "Please search to view records"}
              </div>
            ) : (
              <table className={`w-full table-fixed ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-3 py-3 text-center text-sm w-12">Sel</th>
                    <th className="px-3 py-3 text-left text-sm w-14">S.No</th>
                    <th className="px-3 py-3 text-left text-sm">Vehicle Code</th>
                    <th className="px-3 py-3 text-left text-sm">
                      {isSpares ? "SL.No" : "Vehicle Number"}
                    </th>
                    <th className="px-3 py-3 text-left text-sm">Status</th>
                    <th className="px-3 py-3 text-center text-sm w-16">View</th>
                    <th className="px-3 py-3 text-center text-sm">Approved By</th>
                  </tr>
                </thead>
                <tbody className={isDark ? "divide-gray-700 divide-y" : "divide-gray-200 divide-y"}>
                  {pageRows.map((r, idx) => {
                    const key = getRowKey(r, pageStart + idx);
                    const alreadyApproved = isApprovedRow(r);
                    const statusText =
                      col(r, "Status", "status", "fld_status") ||
                      (alreadyApproved ? "Approved" : "Waiting");
                    return (
                      <tr
                        key={key}
                        className={isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}
                      >
                        <td className="px-3 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={!!checked[key]}
                            disabled={alreadyApproved || isBusy}
                            onChange={() => toggleRow(key)}
                          />
                        </td>
                        <td className="px-3 py-3 text-sm">{pageStart + idx + 1}</td>
                        <td className="px-3 py-3 text-sm font-medium truncate">
                          {col(r, "fld_vehicle_code", "fld_sparesvehiclecode", "code") || "-"}
                        </td>
                        <td className="px-3 py-3 text-sm truncate">
                          {isSpares
                            ? col(r, "fld_slno", "description") || "-"
                            : col(r, "fld_vehicle_regno", "vehicleno") || "-"}
                        </td>
                        <td
                          className={`px-3 py-3 text-sm font-semibold ${
                            String(statusText).toLowerCase().includes("approved")
                              ? "text-green-600"
                              : "text-yellow-600"
                          }`}
                        >
                          {statusText}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => setViewRow(r)}
                            className="text-blue-500 hover:text-blue-600 text-lg"
                            title="View"
                          >
                            <FaEye />
                          </button>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <input
                            type="text"
                            value={
                              approvedBy[key] ||
                              col(r, "fld_vehicle_Approvedby", "approvedBy") ||
                              ""
                            }
                            onChange={(e) =>
                              setApprovedBy((prev) => ({ ...prev, [key]: e.target.value }))
                            }
                            className={`${inputClass} py-1`}
                            placeholder="Approved By"
                            disabled={alreadyApproved || approveLoading}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {totalRecords > 0 && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-4">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Showing {pageStart + 1} - {pageEnd} of {totalRecords}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Rows
                </label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value) || 10);
                    setPage(1);
                  }}
                  className={`${inputClass} w-24 py-1`}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`${pagerBtn(false)} disabled:opacity-40`}
                >
                  <FaChevronLeft />
                </button>
                {pageNumbers.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className={pagerBtn(n === currentPage)}
                  >
                    {n}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className={`${pagerBtn(false)} disabled:opacity-40`}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}

          {viewRow && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
              onClick={() => setViewRow(null)}
            >
              <div
                className={`w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl border ${
                  isDark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-200 text-gray-900"
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                  <h3 className="text-xl font-bold">Transfer Details</h3>
                  <button type="button" onClick={() => setViewRow(null)} className={`p-2 rounded-lg ${isDark ? "hover:bg-gray-800" : "hover:bg-gray-100"}`}>
                    <FaTimes />
                  </button>
                </div>
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ["Vehicle Main Type", col(viewRow, "VehicleType", "fld_vehicletypename", "vehicleType")],
                    ["Vehicle Code", col(viewRow, "fld_vehicle_code", "fld_sparesvehiclecode", "code")],
                    [isSpares ? "SL.No" : "Vehicle Number", isSpares ? col(viewRow, "fld_slno", "description") : col(viewRow, "fld_vehicle_regno", "vehicleno")],
                    ["Date", col(viewRow, "fld_transferdate", "fld_TransferDate", "fld_vehicle_date", "date")],
                    ["Transfer From", col(viewRow, "fld_frombranchname", "fld_frombranch", "frombranch")],
                    ["Transfer To", col(viewRow, "fld_BranchName", "fld_tobranch", "tobranch")],
                    ["From Location", col(viewRow, "fromlocationname", "fld_fromlocationname")],
                    ["To Location", col(viewRow, "tolocationname", "fld_tolocationname")],
                    ["Transfer By", col(viewRow, "fld_username", "transferBy")],
                    ["Status", col(viewRow, "Status", "status", "fld_status")],
                    ["Approved By", col(viewRow, "fld_vehicle_Approvedby", "approvedBy")],
                  ].map(([label, value]) => (
                    <div key={label} className={`p-3 rounded-lg border ${isDark ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
                      <div className={`text-xs font-semibold uppercase mb-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{label}</div>
                      <div className="text-sm font-medium break-words">{value || "-"}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-start mt-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                isDark
                  ? "bg-gray-700 text-white border-gray-600"
                  : "bg-gray-100 text-gray-700 border-gray-300"
              }`}
            >
              <FaArrowLeft /> Back
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTransferApproval;